import type { MaterialSuggestion, LogWarningLevel, Diary, Season, Field } from '@/types';

const PEST_KEYWORDS = ['sâu', 'bệnh', 'rầy', 'sùng', 'nhện', 'nấm', 'thối', 'héo', 'cháy lá', 'đạo ôn', 'vàng lá', 'phun thuốc'];
const DRY_KEYWORDS = ['khô hạn', 'thiếu nước', 'hạn', 'khô', 'nắng nóng', 'tưới'];
const FERTILIZER_KEYWORDS = ['thiếu phân', 'bón phân', 'bón thúc', 'phân bón', 'npk', 'urê', 'kali', 'lân', 'dinh dưỡng', 'còi cọc'];
const YIELD_KEYWORDS = ['giảm năng suất', 'sản lượng thấp', 'mất mùa', 'thất thu'];
const PHOTO_KEYWORDS = ['chụp ảnh', 'hình ảnh', 'ảnh'];

export const WARNING_LEVELS: Record<LogWarningLevel, { label: string; bg: string; border: string; textColor: string; iconColor: string; icon: string }> = {
  none: { label: '', bg: '', border: '', textColor: '', iconColor: '', icon: '' },
  caution: { label: 'Cần chú ý', bg: 'bg-amber-50', border: 'border-amber-200', textColor: 'text-amber-700', iconColor: 'text-amber-600', icon: 'ri-alert-line' },
  check: { label: 'Cần kiểm tra', bg: 'bg-orange-50', border: 'border-orange-200', textColor: 'text-orange-700', iconColor: 'text-orange-600', icon: 'ri-error-warning-line' },
  danger: { label: 'Rủi ro cao', bg: 'bg-red-50', border: 'border-red-200', textColor: 'text-red-700', iconColor: 'text-red-600', icon: 'ri-error-warning-fill' },
};

export const WARNING_TAG_CONFIG: Record<string, { label: string; icon: string; level: LogWarningLevel }> = {
  pest: { label: 'Sâu bệnh', icon: 'ri-bug-line', level: 'danger' },
  dry: { label: 'Thiếu nước', icon: 'ri-drop-line', level: 'check' },
  fertilizer: { label: 'Thiếu phân', icon: 'ri-flask-line', level: 'check' },
  yield_decline: { label: 'Sản lượng giảm', icon: 'ri-seedling-line', level: 'danger' },
  missing_photo: { label: 'Thiếu ảnh thực địa', icon: 'ri-camera-off-line', level: 'caution' },
  missing_log: { label: 'Thiếu nhật ký', icon: 'ri-file-list-2-line', level: 'caution' },
  missing_fertilizer_note: { label: 'Thiếu ghi chú phân bón', icon: 'ri-flask-line', level: 'check' },
};

export function getAllWarnings(diary: Diary, allDiaries: Diary[], season?: Season): { level: LogWarningLevel; tags: string[] } {
  const tags: string[] = [];
  const text = (diary.chiTiet + ' ' + (diary.materialsNote || '')).toLowerCase();

  if (PEST_KEYWORDS.some((k) => text.includes(k))) tags.push('pest');
  if (DRY_KEYWORDS.some((k) => text.includes(k))) tags.push('dry');
  if (FERTILIZER_KEYWORDS.some((k) => text.includes(k) && !text.includes('bón phân') && !text.includes('bón thúc'))) tags.push('fertilizer');
  if (YIELD_KEYWORDS.some((k) => text.includes(k))) tags.push('yield_decline');

  const hasImages = (diary.hinhAnhList && diary.hinhAnhList.length > 0) || !!diary.hinhAnh;
  if (!hasImages && PEST_KEYWORDS.some((k) => text.includes(k))) tags.push('missing_photo');

  let level: LogWarningLevel = 'none';
  if (tags.includes('pest') || tags.includes('yield_decline')) level = 'danger';
  else if (tags.includes('dry') || tags.includes('fertilizer')) level = 'check';
  else if (tags.includes('missing_photo') || tags.includes('missing_log')) level = 'caution';

  return { level, tags };
}

/**
 * `daysSinceLastLog === null` means there is nothing to be late for — no diary has
 * ever been written and no season is under way. The caller used to pass 999 as a
 * stand-in for "never", so the first screen a brand-new farmer saw was a red
 * "Đã 999 ngày không ghi nhật ký. Rủi ro cao" on an account minutes old.
 */
export function getGlobalWarningLevel(daysSinceLastLog: number | null, activeLogs: Diary[]): { level: LogWarningLevel; message: string } | null {
  if (daysSinceLastLog === null) return null;
  if (daysSinceLastLog >= 14) return { level: 'danger', message: `Đã ${daysSinceLastLog} ngày không ghi nhật ký. Rủi ro cao - vui lòng cập nhật ngay!` };
  if (daysSinceLastLog >= 7) return { level: 'check', message: `Đã ${daysSinceLastLog} ngày không ghi nhật ký. Cần kiểm tra và cập nhật.` };
  if (daysSinceLastLog >= 3) return { level: 'caution', message: `Đã ${daysSinceLastLog} ngày không ghi nhật ký. Hãy cập nhật hôm nay.` };
  return null;
}

export function getFertilizerMissingWarning(allDiaries: Diary[], seasons: Season[]): { level: LogWarningLevel; message: string } | null {
  const activeSeasonIds = seasons.filter((s) => s.status === 'Đang canh tác').map((s) => s.id);
  const seasonDiaries = allDiaries.filter((d) => activeSeasonIds.includes(d.seasonId || ''));
  const hasFertilizer = seasonDiaries.some((d) => {
    const text = (d.chiTiet + ' ' + (d.materialsNote || '')).toLowerCase();
    return FERTILIZER_KEYWORDS.some((k) => text.includes(k));
  });

  if (activeSeasonIds.length > 0 && !hasFertilizer) {
    return { level: 'check', message: 'Chưa có ghi chú bón phân cho mùa vụ hiện tại. Cần bổ sung thông tin dinh dưỡng cho cây trồng.' };
  }
  return null;
}

export interface ParsedMaterial {
  tenVatTu: string;
  nhomVatTu: string;
  soLuong: number;
  donVi: string;
}

const MATERIAL_PATTERNS: { regex: RegExp; nhom: string; donVi: string }[] = [
  { regex: /(NPK[\s\w\d-]*)/i, nhom: 'Phân bón', donVi: 'kg' },
  { regex: /(Urê[\s\d%]*|Urea[\s\d%]*)/i, nhom: 'Phân bón', donVi: 'kg' },
  { regex: /(Kali[\s\d%]*)/i, nhom: 'Phân bón', donVi: 'kg' },
  { regex: /(lân[\s\d]*|phân lân[\s\w\d-]*)/i, nhom: 'Phân bón', donVi: 'kg' },
  { regex: /(phân\s?(bón|hữu cơ|chuồng|vi sinh)[\s\w\d-]*)/i, nhom: 'Phân bón', donVi: 'kg' },
  { regex: /(thuốc\s?(BVTV|trừ sâu|trừ rầy|trừ cỏ|bảo vệ|phòng bệnh|đặc trị)[\s\w\d-]*)/i, nhom: 'Thuốc BVTV', donVi: 'lít' },
  { regex: /(giống\s?(lúa|cà phê|rau|màu|sầu riêng|xoài|mít)[\s\w\d-]*)/i, nhom: 'Giống', donVi: 'kg' },
  { regex: /(bao bì[\s\w\d]*)/i, nhom: 'Bao bì', donVi: 'cái' },
];

function parseAmount(text: string, defaultUnit: string): { soLuong: number; donVi: string } {
  const amountMatch = text.match(/(\d+\.?\d*)\s*(kg|lít|chai|cái|gói|bao|cây|ha)/i);
  if (amountMatch) {
    return { soLuong: parseFloat(amountMatch[1]), donVi: amountMatch[2].toLowerCase() };
  }
  const numMatch = text.match(/(\d+\.?\d*)/);
  if (numMatch) {
    return { soLuong: parseFloat(numMatch[1]), donVi: defaultUnit };
  }
  return { soLuong: 1, donVi: defaultUnit };
}

export function parseMaterialsNote(note: string, seasonId?: string): { suggestions: MaterialSuggestion[]; summary: string } {
  const suggestions: MaterialSuggestion[] = [];
  const foundSegments: string[] = [];

  const parts = note.split(/[,;.]+/);

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    let matched = false;
    for (const pattern of MATERIAL_PATTERNS) {
      const match = trimmed.match(pattern.regex);
      if (match) {
        const name = match[0].trim();
        const { soLuong, donVi } = parseAmount(trimmed, pattern.donVi);
        const existingIdx = suggestions.findIndex((s) => s.tenVatTu.toLowerCase() === name.toLowerCase());
        if (existingIdx >= 0) {
          suggestions[existingIdx].soLuong += soLuong;
        } else {
          suggestions.push({
            tenVatTu: name.charAt(0).toUpperCase() + name.slice(1),
            nhomVatTu: pattern.nhom,
            soLuong,
            donVi,
            seasonId,
            applied: false,
          });
        }
        foundSegments.push(name);
        matched = true;
        break;
      }
    }
    if (!matched) foundSegments.push(trimmed);
  }

  const summary = suggestions.length > 0
    ? `Đã nhận diện ${suggestions.length} vật tư: ${suggestions.map((s) => `${s.tenVatTu} (${s.soLuong} ${s.donVi})`).join(', ')}`
    : '';

  return { suggestions, summary };
}